// Keep type imports only at the top level
import type { AnimationItem, CanvasRendererConfig, HTMLRendererConfig, SVGRendererConfig } from 'lottie-web';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// Keep interfaces accessible for consumers
export interface LottieAnimationHandle {
  play: () => void;
  stop: () => void;
  // Expose goToAndPlay, mirrors lottie-web's AnimationItem method
  goToAndPlay: (value: number | string, isFrame?: boolean) => void;
  // Add other Lottie methods you might need to expose
}

// Define valid renderer types
type RendererType = 'svg' | 'canvas' | 'html';

// Define renderer settings based on renderer type
type RendererSettings = SVGRendererConfig | CanvasRendererConfig | HTMLRendererConfig;

// Define base props
export interface LottieBaseProps {
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
  onComplete?: () => void;
  renderer?: RendererType;
  rendererSettings?: RendererSettings;
  colorMap?: Record<string, [number, number, number, number]>;
}

// Define props when using path
interface LottieWithPathProps extends LottieBaseProps {
  path: string;
  animationData?: never; // Ensure animationData is not provided when path is
}

// Define props when using animationData
interface LottieWithDataProps extends LottieBaseProps {
  path?: never; // Ensure path is not provided when animationData is
  animationData: object;
}

// Use a union type for the component props
export type LottieAnimationProps = LottieWithPathProps | LottieWithDataProps;

export const LottieAnimation = forwardRef<LottieAnimationHandle, LottieAnimationProps>(
  ({ path, animationData, className, loop = true, autoplay = true, onComplete, renderer = 'svg', rendererSettings }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const animationInstanceRef = useRef<AnimationItem | null>(null);
    const isLoadingRef = useRef(false); // Track loading status to prevent race conditions
    const isMountedRef = useRef(true); // Track if component is still mounted

    useEffect(() => {
      // Set mounted status
      isMountedRef.current = true;

      // Only create animation if container exists, not already loading, and no animation exists
      if (containerRef.current && !isLoadingRef.current && !animationInstanceRef.current) {
        isLoadingRef.current = true;

        // Dynamically import lottie-web
        import('lottie-web')
          .then(lottie => {
            // Double-check component is still mounted and no animation was created during import
            if (!isMountedRef.current || !containerRef.current || animationInstanceRef.current) {
              isLoadingRef.current = false;
              return;
            }

            const lottieInstance = lottie.default; // Access the default export

            const baseConfig = {
              container: containerRef.current,
              renderer: renderer,
              loop: loop,
              autoplay: autoplay,
              rendererSettings: rendererSettings
            };

            let animation: AnimationItem;

            try {
              if (path) {
                animation = lottieInstance.loadAnimation({
                  ...baseConfig,
                  path: path
                });
              } else if (animationData) {
                animation = lottieInstance.loadAnimation({
                  ...baseConfig,
                  animationData: animationData
                });
              } else {
                console.error("LottieAnimation requires either 'path' or 'animationData' prop.");
                isLoadingRef.current = false;
                return;
              }

              // Final check before storing the animation
              if (!isMountedRef.current) {
                animation.destroy();
                isLoadingRef.current = false;
                return;
              }

              animationInstanceRef.current = animation;

              // Add event listener for completion if onComplete is provided
              if (onComplete) {
                animation.addEventListener('complete', onComplete);
              }
            } catch (error) {
              console.error('Failed to create Lottie animation:', error);
            }

            isLoadingRef.current = false;
          })
          .catch(error => {
            console.error('Failed to import lottie-web:', error);
            isLoadingRef.current = false;
          });
      }

      // Cleanup function
      return () => {
        isMountedRef.current = false;

        // Destroy existing animation if it exists
        if (animationInstanceRef.current) {
          // Remove event listener if onComplete exists
          if (onComplete) {
            animationInstanceRef.current.removeEventListener('complete', onComplete);
          }
          animationInstanceRef.current.destroy();
          animationInstanceRef.current = null;
        }

        // Reset loading state
        isLoadingRef.current = false;
      };
    }, [path, animationData, loop, autoplay, onComplete, renderer, rendererSettings]);

    useImperativeHandle(ref, () => ({
      play: () => {
        // Ensure lottie is loaded before calling methods
        animationInstanceRef.current?.play();
      },
      stop: () => {
        animationInstanceRef.current?.stop();
      },
      // Implement goToAndPlay
      goToAndPlay: (value, isFrame) => {
        animationInstanceRef.current?.goToAndPlay(value, isFrame);
      }
      // Add other Lottie methods implementations here
    }));

    return <div ref={containerRef} className={className} />;
  }
);

LottieAnimation.displayName = 'LottieAnimation';
